/**
 * Numeros: arredondamento, quantizacao e formatacao pt-BR.
 *
 * A quantizacao aqui nao e cosmetica. `Math.sin`, `Math.cos`, `Math.pow`, `Math.exp`
 * e `Math.log` sao *implementation-defined* em ECMAScript: podem diferir no ultimo ulp
 * entre Chrome e Firefox, ou entre versoes de Node. Logo, "instancia byte-identica"
 * e falso sobre doubles crus.
 *
 * Toda coordenada e todo valor numerico passam por `quantize` ANTES de serializar e
 * ANTES de hashear. O quantum de 1e-6 fica ~10 ordens de grandeza acima do ruido de
 * ulp, entao a diferenca entre motores desaparece. Consequencia a respeitar: nunca
 * publique mais precisao do que voce quantiza.
 */

/** Casas decimais usadas na canonizacao de coordenadas e valores. */
export const CANONICAL_DP = 6;

export function roundTo(x: number, dp = 0): number {
  if (!Number.isFinite(x)) return x;
  const f = 10 ** dp;
  // O `+ Number.EPSILON * Math.sign(x)` corrige o classico 1.005 -> 1.00.
  return Math.round((x + Number.EPSILON * Math.sign(x)) * f) / f;
}

/** Canonizacao para serializacao/hash. Normaliza `-0` para `0`. */
export function quantize(x: number, dp = CANONICAL_DP): number {
  if (!Number.isFinite(x)) return x;
  const q = roundTo(x, dp);
  return Object.is(q, -0) ? 0 : q;
}

/** Maximo divisor comum de inteiros nao-negativos. */
export function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x;
}

/** Quantas casas decimais o numero realmente usa (ate `max`). */
export function decimalPlaces(x: number, max = 6): number {
  for (let dp = 0; dp <= max; dp++) {
    if (Math.abs(x - roundTo(x, dp)) < 1e-9) return dp;
  }
  return max;
}

/**
 * Formata em pt-BR: vírgula decimal, ponto de milhar e o SINAL DE MENOS TIPOGRÁFICO (−).
 *
 * O `toLocaleString` escreve o menos com hífen — um traço, não um sinal — e escreve
 * "-0" para o zero negativo e para o que arredonda a zero vindo de baixo ("-0,00").
 * Cada chamador protegia o próprio texto com `v < 0 ? `−${formatBR(-v)}` : …`; eram
 * quinze cópias dessa guarda, e nenhuma pegava o "-0". Agora a guarda mora aqui: o que
 * arredonda a zero sai sem sinal.
 */
export function formatBR(x: number, dp?: number): string {
  if (!Number.isFinite(x)) return x.toLocaleString('pt-BR');
  const casas = dp ?? decimalPlaces(x);
  const s = Math.abs(x).toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
  return x < 0 && /[1-9]/.test(s) ? `−${s}` : s;
}

/**
 * O NÚMERO QUE O ALUNO DIGITOU, lido como ele escreve.
 *
 * A versão anterior só trocava a vírgula por ponto. Com isso "4.800" — quatro mil e
 * oitocentos reais, como se escreve dinheiro no Brasil — era lido como 4,8, e a
 * resposta certa do item de reajustes era corrigida como errada; "4.800,00" nem era
 * número. As regras, na ordem:
 *
 *  - com vírgula, ela é o decimal e os pontos são milhar: "1.234,5" → 1234,5;
 *  - sem vírgula, ponto seguido de grupos de TRÊS algarismos é milhar: "4.800" → 4800,
 *    "1.234.567" → 1234567 — desde que não comece por zero: "0.500" é meio;
 *  - qualquer outro ponto é decimal: "12.5" → 12,5, de quem aprendeu assim ou digita
 *    num teclado em inglês.
 *
 * A ambiguidade que sobra ("1.250" é mil duzentos e cinquenta, e nunca 1,25) é
 * resolvida pelo banco: nenhuma resposta digitada tem três casas decimais.
 *
 * Aceita também o que o aluno escreve junto do número sem estar errado: o menos
 * tipográfico (−) e o travessão, espaços, "R$" na frente, "%" atrás e a unidade do
 * próprio item ("450 km"). Recusa o que `Number` aceitaria mas ninguém digita numa
 * prova — "1e3", "0x10", "Infinity".
 */
export function lerNumeroBR(texto: string, unidade?: string): number | null {
  let s = texto.trim().replace(/[\s ]/g, '');
  s = s.replace(/[−–]/g, '-').replace(/^R\$/i, '').replace(/%$/, '');
  if (unidade) {
    const u = unidade.replace(/[\s ]/g, '').toLowerCase();
    if (u !== '' && s.toLowerCase().endsWith(u)) s = s.slice(0, -u.length);
  }
  if (s === '') return null;

  if (s.includes(',')) {
    const partes = s.split(',');
    if (partes.length !== 2) return null;
    const [inteira, fracao] = partes as [string, string];
    if (!/^[+-]?(\d{1,3}(\.\d{3})+|\d*)$/.test(inteira) || !/^\d+$/.test(fracao)) return null;
    return Number(`${inteira.replace(/\./g, '')}.${fracao}`);
  }
  if (/^[+-]?[1-9]\d{0,2}(\.\d{3})+$/.test(s)) return Number(s.replace(/\./g, ''));
  if (!/^[+-]?(\d+(\.\d+)?|\.\d+)$/.test(s)) return null;
  return Number(s);
}

/**
 * Separacao relativa entre dois valores, normalizada pela maior magnitude.
 * Usada para impedir dois distratores "praticamente identicos" (regra do catalogo).
 */
export function relativeSeparation(a: number, b: number): number {
  const m = Math.max(Math.abs(a), Math.abs(b));
  if (m < 1e-12) return Math.abs(a - b) < 1e-12 ? 0 : Infinity;
  return Math.abs(a - b) / m;
}

/** A sequencia forma uma progressao aritmetica? (o catalogo proibe: sugere "a do meio") */
export function isArithmeticProgression(xs: readonly number[], tol = 1e-6): boolean {
  if (xs.length < 3) return false;
  const d = (xs[1] as number) - (xs[0] as number);
  if (Math.abs(d) < 1e-12) return false;
  for (let i = 2; i < xs.length; i++) {
    const step = (xs[i] as number) - (xs[i - 1] as number);
    if (relativeSeparation(step, d) > tol) return false;
  }
  return true;
}

/** A sequencia forma uma progressao geometrica? */
export function isGeometricProgression(xs: readonly number[], tol = 1e-6): boolean {
  if (xs.length < 3) return false;
  if (xs.some((x) => Math.abs(x) < 1e-12)) return false;
  const r = (xs[1] as number) / (xs[0] as number);
  if (Math.abs(r - 1) < 1e-12) return false;
  for (let i = 2; i < xs.length; i++) {
    const ratio = (xs[i] as number) / (xs[i - 1] as number);
    if (relativeSeparation(ratio, r) > tol) return false;
  }
  return true;
}
